import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { useT, useLang } from "@/lib/i18n";

export default function Cards() {
  const t = useT();
  const { lang } = useLang();
  const [cards, setCards] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ external_id: "", title: "", source: "azure_devops" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/cards");
    setCards(data.cards);
  };
  useEffect(() => {
    load();
    const tt = setInterval(load, 2000);
    return () => clearInterval(tt);
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/cards", form);
      toast.success("Card queued for analysis");
      setShowNew(false); setForm({ external_id: "", title: "", source: "azure_devops" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-7xl">
      <div className="overline mb-3">{t("cards.overline")}</div>
      <div className="flex justify-between items-end mb-10">
        <h1 className="font-display font-black text-4xl tracking-tighter">{t("cards.title")}</h1>
        <button onClick={() => setShowNew(!showNew)} className="btn-primary text-sm py-2 px-4" data-testid="cards-new-btn">
          <Plus className="w-4 h-4" /> {t("cards.new")}
        </button>
      </div>

      {showNew && (
        <form onSubmit={submit} className="surface p-6 mb-6 space-y-3" data-testid="cards-new-form">
          <div className="overline">{t("cards.newAnalysis")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">{t("cards.extId")}</label>
              <input required value={form.external_id} onChange={(e) => setForm({...form, external_id: e.target.value})}
                placeholder="e.g. 8821934"
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="cards-new-id" />
            </div>
            <div>
              <label className="overline block mb-1">{t("cards.source")}</label>
              <select value={form.source} onChange={(e) => setForm({...form, source: e.target.value})}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="cards-new-source">
                <option value="azure_devops">Azure DevOps / TFS</option>
                <option value="github">GitHub Issues</option>
                <option value="jira">Jira</option>
                <option value="linear">Linear</option>
                <option value="manual">Manual</option>
              </select>
            </div>
          </div>
          <div>
            <label className="overline block mb-1">{t("cards.cardTitle")}</label>
            <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
              placeholder="e.g. Scheduled job didn't fire for tenant X"
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="cards-new-title" />
          </div>
          <div className="flex gap-2">
            <button disabled={loading} className="btn-primary" data-testid="cards-new-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("cards.run")}
            </button>
            <button type="button" onClick={() => setShowNew(false)} className="btn-ghost">{t("common.cancel")}</button>
          </div>
        </form>
      )}

      <div className="surface">
        {cards.length === 0 ? (
          <div className="p-12 text-center text-white/40 text-sm">{t("cards.empty")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-white/10">
              <tr className="text-left">
                <th className="overline p-4">{t("cards.col.status")}</th>
                <th className="overline p-4">{t("cards.col.id")}</th>
                <th className="overline p-4">{t("cards.col.title")}</th>
                <th className="overline p-4">{t("cards.col.scenario")}</th>
                <th className="overline p-4">KB</th>
                <th className="overline p-4">{t("cards.col.saved")}</th>
                <th className="overline p-4">{t("cards.col.date")}</th>
              </tr>
            </thead>
            <tbody>
              {cards.map((c) => {
                const kbUsed = Number(c.report?.kb_patterns_used || 0);
                return (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <Link to={`/app/cards/${c.id}`} className="flex items-center gap-2" data-testid={`cards-row-${c.id}`}>
                      <span className={`status-dot ${c.status === "complete" ? "bg-emerald-400" : c.status === "awaiting_user" ? "bg-white/40" : "bg-amber-400 animate-pulse"}`} />
                      <span className="font-mono text-xs">{c.status}</span>
                    </Link>
                  </td>
                  <td className="p-4 font-mono text-xs text-white/70"><Link to={`/app/cards/${c.id}`}>{c.external_id}</Link></td>
                  <td className="p-4"><Link to={`/app/cards/${c.id}`}>{c.title}</Link></td>
                  <td className="p-4 font-mono text-xs"><span className="px-2 py-0.5 bg-white/5">{c.scenario}</span></td>
                  <td className="p-4 font-mono text-xs">
                    {kbUsed > 0 ? (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-400/10 text-emerald-400 border border-emerald-400/20 rounded-sm"
                        data-testid={`cards-row-kb-${c.id}`}
                        title={lang === "pt"
                          ? `${kbUsed} padrões históricos da KB usados nesta investigação`
                          : `${kbUsed} historical KB patterns used in this investigation`}
                      >
                        <Zap className="w-2.5 h-2.5" />
                        {kbUsed}
                      </span>
                    ) : (
                      <span className="text-white/20">—</span>
                    )}
                  </td>
                  <td className="p-4 font-mono text-xs">{c.report ? `${c.report.time_saved_minutes}m` : `${c.progress ?? 0}%`}</td>
                  <td className="p-4 font-mono text-xs text-white/40">{new Date(c.created_at).toLocaleString()}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
